import { useState, useEffect, useMemo } from 'react';
import { 
  AlertTriangle, 
  Search, 
  Filter, 
  CheckSquare, 
  Square, 
  ArrowRight, 
  XCircle, 
  FileText, 
  Loader2, 
  ChevronRight,
  Info,
  RefreshCw,
  Trash2,
  Bug,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Activity,
  CheckCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { ChangeOrderBuilder } from './ChangeOrderBuilder';
import { ScopeDelta, ScopeDeltaDecision } from './ScopeDeltaReview';

// Type extensions for the unified view
interface EnrichedScopeDelta extends ScopeDelta {
  configItemId: string;
  configItemName: string;
  currentDecision: 'reduce_other_scope' | 'client_handles' | 'prepare_change_order' | null;
  decisionId?: string; // If existing decision
  
  // Extra fields for Change Requests integration
  isChangeRequest?: boolean;
  changeRequestStatus?: 'pending' | 'approved' | 'rejected' | 'implemented' | 'open' | 'in_progress' | 'resolved';
  requestedBy?: string;
  createdAt?: string;
  diff?: {
    before: any;
    after: any;
  };
  // Defect specific
  severity?: 'low' | 'medium' | 'high';
  expectedBehavior?: string;
  actualBehavior?: string;
  resolutionStatus?: 'open' | 'in_progress' | 'resolved' | 'rejected';
}

interface ScopeControlCenterProps {
  companyId: string;
  companyName: string;
  accessToken: string;
  currentUser: any;
  onNavigateToConfigItem: (configItemId: string, tab?: string) => void;
  initialFilterStatus?: string;
}

export function ScopeControlCenter({
  companyId,
  companyName,
  accessToken,
  currentUser,
  onNavigateToConfigItem,
  initialFilterStatus
}: ScopeControlCenterProps) {
  // State
  const [loading, setLoading] = useState(true);
  const [enrichedDeltas, setEnrichedDeltas] = useState<EnrichedScopeDelta[]>([]);
  const [selectedDeltaIds, setSelectedDeltaIds] = useState<Set<string>>(new Set());
  const [isDraftingCO, setIsDraftingCO] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [expandedDiffs, setExpandedDiffs] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('scope');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [impactFilter, setImpactFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Handle initial filter
  useEffect(() => {
    if (initialFilterStatus) {
      setStatusFilter(initialFilterStatus);
    }
  }, [initialFilterStatus]);


  // Load Data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch AI-detected Scope Deltas from Config Items
        const detailsResponse = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/config-item-details`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        // 2. Fetch Manual Change Requests
        const crResponse = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${companyId}/change-requests`,
            {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        const processedDeltas: EnrichedScopeDelta[] = [];

        // Process Config Item Deltas
        if (detailsResponse.ok) {
            const { details } = await detailsResponse.json();
            Object.values(details || {}).forEach((item: any) => {
                const deltas = item.artifacts?.scopeDelta || [];
                const decisions = item.artifacts?.scopeDeltaDecisions || {};
                
                deltas.forEach((delta: ScopeDelta) => {
                    const decision = decisions[delta.deltaId];
                    processedDeltas.push({
                        ...delta,
                        configItemId: item.id,
                        configItemName: item.name || 'Unnamed Item',
                        currentDecision: decision?.decision || null,
                        decisionId: decision?.deltaId,
                        isChangeRequest: false
                    });
                });
            });
        }

        // Process Change Requests
        if (crResponse.ok) {
            const { changeRequests } = await crResponse.json();
            (changeRequests || []).forEach((cr: any) => {
                // Map ChangeRequest to ScopeDelta-like structure
                processedDeltas.push({
                    deltaId: cr.id,
                    description: cr.description,
                    deltaType: cr.type, // 'defect' | 'scope_change'
                    estimatedImpact: cr.type === 'defect' ? 'unknown' : 'unknown', 
                    sourceArtifact: cr.type === 'defect' ? 'Defect Report' : 'Change Request',
                    configItemId: cr.requestTypeId,
                    configItemName: cr.requestTypeName || 'Request Type',
                    // Map Status to Decision
                    currentDecision: cr.status === 'pending' ? null : 
                                     cr.status === 'approved' ? 'prepare_change_order' : null,
                    isChangeRequest: true,
                    changeRequestStatus: cr.status,
                    requestedBy: cr.requestedBy?.name,
                    createdAt: cr.createdAt,
                    diff: cr.diff,
                    severity: cr.severity,
                    expectedBehavior: cr.expectedBehavior,
                    actualBehavior: cr.actualBehavior,
                    resolutionStatus: cr.resolutionStatus
                });
            });
        }

        setEnrichedDeltas(processedDeltas);
      } catch (error) {
        console.error('Error loading scope data:', error);
        toast.error('Failed to load scope data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyId, accessToken, refreshTrigger]);

  // Filtering Logic
  const filteredDeltas = useMemo(() => {
    return enrichedDeltas.filter(delta => {
      // Tab Filter
      if (activeTab === 'scope' && delta.deltaType === 'defect') return false;
      if (activeTab === 'defects' && delta.deltaType !== 'defect') return false;

      // Text Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matches = 
          delta.description.toLowerCase().includes(query) ||
          delta.configItemName.toLowerCase().includes(query) ||
          delta.sourceArtifact.toLowerCase().includes(query);
        if (!matches) return false;
      }

      // Status Filter
      if (statusFilter !== 'all') {
        if (activeTab === 'scope') {
            if (statusFilter === 'undecided') {
                if (delta.currentDecision !== null && delta.changeRequestStatus !== 'pending') return false;
            } else {
                if (delta.currentDecision !== statusFilter && delta.changeRequestStatus !== statusFilter) return false;
            }
        } else {
            // Defect Status Filter
            if (delta.resolutionStatus !== statusFilter && delta.changeRequestStatus !== statusFilter) return false;
        }
      }

      // Impact/Severity Filter
      if (impactFilter !== 'all') {
         if (activeTab === 'scope' && delta.estimatedImpact !== impactFilter) return false;
         if (activeTab === 'defects' && delta.severity !== impactFilter) return false;
      }

      // Type Filter (only relevant if mixed, but we split tabs)
      if (typeFilter !== 'all' && delta.deltaType !== typeFilter) return false;

      return true;
    });
  }, [enrichedDeltas, searchQuery, statusFilter, impactFilter, typeFilter, activeTab]);

  // Stats
  const stats = useMemo(() => {
    const scopeItems = enrichedDeltas.filter(d => d.deltaType !== 'defect');
    const defectItems = enrichedDeltas.filter(d => d.deltaType === 'defect');
    
    return {
      scope: {
          total: scopeItems.length,
          undecided: scopeItems.filter(d => d.currentDecision === null && (!d.isChangeRequest || d.changeRequestStatus === 'pending')).length,
          markedForCO: scopeItems.filter(d => d.currentDecision === 'prepare_change_order' || (d.isChangeRequest && d.changeRequestStatus === 'approved')).length,
      },
      defects: {
          total: defectItems.length,
          open: defectItems.filter(d => d.resolutionStatus === 'open' || d.resolutionStatus === 'in_progress').length,
          highSeverity: defectItems.filter(d => d.severity === 'high').length
      }
    };
  }, [enrichedDeltas]);

  // Handlers
  const handleSelectAll = () => {
    if (selectedDeltaIds.size === filteredDeltas.length) {
      setSelectedDeltaIds(new Set());
    } else {
      setSelectedDeltaIds(new Set(filteredDeltas.map(d => d.deltaId)));
    }
  };

  const toggleSelection = (id: string) => {
    const newSet = new Set(selectedDeltaIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedDeltaIds(newSet);
  };

  const toggleDiff = (id: string) => {
    const newSet = new Set(expandedDiffs);
    if (newSet.has(id)) {
        newSet.delete(id);
    } else {
        newSet.add(id);
    }
    setExpandedDiffs(newSet);
  };

  const handleBulkDecision = async (decision: 'reduce_other_scope' | 'client_handles' | 'prepare_change_order' | null) => {
    if (selectedDeltaIds.size === 0) return;

    const toastId = toast.loading(`Updating ${selectedDeltaIds.size} items...`);
    
    // Split into Manual Change Requests vs AI Deltas
    const selectedDeltasArray = enrichedDeltas.filter(d => selectedDeltaIds.has(d.deltaId));
    const aiDeltas = selectedDeltasArray.filter(d => !d.isChangeRequest);
    
    if (aiDeltas.length === 0) {
        toast.info("Selected items are Change Requests. Please manage them individually for now.", { id: toastId });
        return;
    }

    const updatesByItem: Record<string, string[]> = {};
    aiDeltas.forEach(delta => {
        if (!updatesByItem[delta.configItemId]) {
            updatesByItem[delta.configItemId] = [];
        }
        updatesByItem[delta.configItemId].push(delta.deltaId);
    });

    try {
        const promises = Object.entries(updatesByItem).flatMap(([itemId, deltaIds]) => {
            return deltaIds.map(deltaId => {
                if (!decision) return Promise.resolve(); 
                return fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${itemId}/scope-deltas/decisions`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        companyId,
                        deltaId,
                        decision,
                        userId: currentUser.id,
                        userName: currentUser.name || currentUser.email,
                        notes: 'Bulk updated via Scope Control Center'
                    })
                });
            });
        });

        await Promise.all(promises);
        toast.success(`Updated ${aiDeltas.length} items`, { id: toastId });
        setRefreshTrigger(prev => prev + 1);
        setSelectedDeltaIds(new Set()); 
    } catch (error) {
        console.error("Bulk update failed", error);
        toast.error("Some updates failed", { id: toastId });
    }
  };

  const handleDraftChangeOrder = () => {
    setIsDraftingCO(true);
  };

  const getDeltasForCO = () => {
    if (selectedDeltaIds.size > 0) {
        return enrichedDeltas.filter(d => selectedDeltaIds.has(d.deltaId));
    } else {
        return enrichedDeltas.filter(d => d.currentDecision === 'prepare_change_order' || (d.isChangeRequest && d.changeRequestStatus === 'approved'));
    }
  };

  const handleUpdateDefectStatus = async (deltaId: string, newStatus: string) => {
      const toastId = toast.loading("Updating defect status...");
      try {
        const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/change-request/${deltaId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                resolutionStatus: newStatus,
                userRole: currentUser.role // simplified
            })
        });

        if (response.ok) {
            toast.success("Updated", { id: toastId });
            setRefreshTrigger(prev => prev + 1);
        } else {
            toast.error("Failed to update", { id: toastId });
        }
      } catch (e) {
        toast.error("Network error", { id: toastId });
      }
  };

  if (isDraftingCO) {
    const coDeltas = getDeltasForCO();
    return (
        <ChangeOrderBuilder
            selectedDeltas={coDeltas}
            companyId={companyId}
            configItemId={coDeltas[0]?.configItemId || ''} 
            userId={currentUser.id}
            userName={currentUser.name || currentUser.email}
            onBack={() => setIsDraftingCO(false)}
            isMultiItemMode={true} 
        />
    );
  }

  // Diff Renderer
  const renderDiff = (diff: { before: any, after: any }) => {
    const keys = new Set([...Object.keys(diff.before || {}), ...Object.keys(diff.after || {})]);
    return (
        <div className="bg-slate-50 p-3 rounded-md border text-xs mt-2 space-y-2 animate-in fade-in slide-in-from-top-1">
            <h4 className="text-xs font-semibold text-slate-700">Detailed Changes:</h4>
            {Array.from(keys).map(key => {
                const valBefore = diff.before?.[key];
                const valAfter = diff.after?.[key];
                return (
                    <div key={key} className="grid grid-cols-[120px_1fr_20px_1fr] gap-2 items-start py-1 border-b border-slate-100 last:border-0">
                        <span className="font-medium text-slate-600 truncate" title={key}>{key}</span>
                        <div className="bg-red-50 text-red-700 px-2 py-0.5 rounded break-all">
                            {typeof valBefore === 'object' ? JSON.stringify(valBefore).slice(0, 100) : String(valBefore ?? '—')}
                        </div>
                        <span className="text-slate-400 text-center">→</span>
                        <div className="bg-green-50 text-green-700 px-2 py-0.5 rounded break-all">
                            {typeof valAfter === 'object' ? JSON.stringify(valAfter).slice(0, 100) : String(valAfter ?? '—')}
                        </div>
                    </div>
                );
            })}
        </div>
    );
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      {/* Header & Stats */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-start">
            <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                    Scope Control Center
                </h2>
                <p className="text-muted-foreground">
                    Review and manage scope creep across the entire engagement.
                </p>
            </div>
            <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRefreshTrigger(prev => prev + 1)}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                </Button>
                {activeTab === 'scope' && (
                    <Button 
                        onClick={handleDraftChangeOrder}
                        disabled={stats.scope.markedForCO === 0 && selectedDeltaIds.size === 0}
                    >
                        <FileText className="w-4 h-4 mr-2" />
                        Draft Change Order
                    </Button>
                )}
            </div>
        </div>

        {/* Stats Cards based on Active Tab */}
        <div className="grid grid-cols-4 gap-4">
            {activeTab === 'scope' ? (
                <>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold">{stats.scope.total}</div>
                            <p className="text-xs text-muted-foreground uppercase font-semibold">Scope Deltas</p>
                        </CardContent>
                    </Card>
                    <Card className={stats.scope.undecided > 0 ? "border-orange-200 bg-orange-50" : ""}>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold text-orange-700">{stats.scope.undecided}</div>
                            <p className="text-xs text-orange-700 uppercase font-semibold">Undecided</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold text-blue-600">{stats.scope.markedForCO}</div>
                            <p className="text-xs text-muted-foreground uppercase font-semibold">Marked for CO</p>
                        </CardContent>
                    </Card>
                </>
            ) : (
                <>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold">{stats.defects.total}</div>
                            <p className="text-xs text-muted-foreground uppercase font-semibold">Total Defects</p>
                        </CardContent>
                    </Card>
                    <Card className={stats.defects.open > 0 ? "border-red-200 bg-red-50" : ""}>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold text-red-700">{stats.defects.open}</div>
                            <p className="text-xs text-red-700 uppercase font-semibold">Open / In Progress</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold text-red-600">{stats.defects.highSeverity}</div>
                            <p className="text-xs text-muted-foreground uppercase font-semibold">High Severity</p>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
      </div>

      <Separator />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList>
            <TabsTrigger value="scope" className="flex gap-2">
                <FileText className="w-4 h-4" />
                Scope Changes & Decisions
                {stats.scope.undecided > 0 && <Badge variant="secondary" className="ml-1 px-1 h-5">{stats.scope.undecided}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="defects" className="flex gap-2">
                <Bug className="w-4 h-4" />
                Defects & Quality
                {stats.defects.open > 0 && <Badge variant="secondary" className="ml-1 px-1 h-5 bg-red-100 text-red-700">{stats.defects.open}</Badge>}
            </TabsTrigger>
        </TabsList>

        <div className="flex items-center justify-between gap-4 bg-background sticky top-0 z-10 py-2 border-b mt-2">
            <div className="flex items-center gap-2 flex-1">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search..."
                        className="pl-8"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        {activeTab === 'scope' ? (
                            <>
                                <SelectItem value="undecided">Undecided</SelectItem>
                                <SelectItem value="prepare_change_order">For Change Order</SelectItem>
                            </>
                        ) : (
                            <>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="resolved">Resolved</SelectItem>
                            </>
                        )}
                    </SelectContent>
                </Select>
                
                {activeTab === 'scope' ? (
                     <Select value={impactFilter} onValueChange={setImpactFilter}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Impact" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Impacts</SelectItem>
                            <SelectItem value="large">High Impact</SelectItem>
                            <SelectItem value="medium">Medium Impact</SelectItem>
                            <SelectItem value="small">Low Impact</SelectItem>
                        </SelectContent>
                    </Select>
                ) : (
                    <Select value={impactFilter} onValueChange={setImpactFilter}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Severity" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Severities</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                    </Select>
                )}
            </div>
            
            {activeTab === 'scope' && selectedDeltaIds.size > 0 && (
                <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4">
                    <span className="text-sm text-muted-foreground mr-2">{selectedDeltaIds.size} selected</span>
                    <Button size="sm" variant="outline" onClick={() => handleBulkDecision('reduce_other_scope')}>
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Swap
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleBulkDecision('client_handles')}>
                        <XCircle className="w-4 h-4 mr-2" />
                        Client
                    </Button>
                    <Button size="sm" variant="default" onClick={() => handleBulkDecision('prepare_change_order')}>
                        <FileText className="w-4 h-4 mr-2" />
                        Add to CO
                    </Button>
                </div>
            )}
        </div>

        <TabsContent value="scope" className="flex-1 overflow-auto bg-white border rounded-md m-0">
             {loading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
            ) : filteredDeltas.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                    <Info className="w-12 h-12 mb-4 opacity-20" />
                    <p>No scope deltas found matching your filters.</p>
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0 z-10">
                        <tr className="border-b">
                            <th className="p-4 w-[40px]">
                                <Button variant="ghost" size="icon" className="h-4 w-4 p-0" onClick={handleSelectAll}>
                                    {selectedDeltaIds.size > 0 && selectedDeltaIds.size === filteredDeltas.length ? (
                                        <CheckSquare className="h-4 w-4" />
                                    ) : (
                                        <Square className="h-4 w-4" />
                                    )}
                                </Button>
                            </th>
                            <th className="p-4 text-left font-medium">Config Item / Delta</th>
                            <th className="p-4 text-left font-medium w-[120px]">Impact</th>
                            <th className="p-4 text-left font-medium w-[180px]">Status</th>
                            <th className="p-4 w-[100px]"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {filteredDeltas.map(delta => (
                            <tr key={delta.deltaId} className={selectedDeltaIds.has(delta.deltaId) ? "bg-muted/30" : ""}>
                                <td className="p-4 align-top">
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-4 w-4 p-0" 
                                        onClick={() => toggleSelection(delta.deltaId)}
                                    >
                                        {selectedDeltaIds.has(delta.deltaId) ? (
                                            <CheckSquare className="h-4 w-4 text-primary" />
                                        ) : (
                                            <Square className="h-4 w-4 text-muted-foreground" />
                                        )}
                                    </Button>
                                </td>
                                <td className="p-4 align-top">
                                    <div className="font-medium text-foreground">{delta.description}</div>
                                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                                        <Badge variant="outline" className="font-normal bg-slate-50">
                                            {delta.configItemName}
                                        </Badge>
                                        <span>via {delta.sourceArtifact}</span>
                                        {delta.isChangeRequest && delta.requestedBy && (
                                            <span>• By {delta.requestedBy}</span>
                                        )}
                                    </div>
                                    {delta.diff && expandedDiffs.has(delta.deltaId) && renderDiff(delta.diff)}
                                </td>
                                <td className="p-4 align-top">
                                    <Badge variant={
                                        delta.estimatedImpact === 'large' ? 'destructive' : 
                                        delta.estimatedImpact === 'medium' ? 'default' : 'secondary'
                                    }>
                                        {delta.estimatedImpact.toUpperCase()}
                                    </Badge>
                                </td>
                                <td className="p-4 align-top">
                                    {delta.isChangeRequest ? (
                                         <Badge variant="outline" className={`
                                            ${delta.changeRequestStatus === 'pending' ? 'bg-orange-50 text-orange-700' : ''}
                                            ${delta.changeRequestStatus === 'approved' ? 'bg-green-50 text-green-700' : ''}
                                            ${delta.changeRequestStatus === 'rejected' ? 'bg-red-50 text-red-700' : ''}
                                         `}>
                                            {delta.changeRequestStatus ? delta.changeRequestStatus.toUpperCase() : 'PENDING'}
                                         </Badge>
                                    ) : (
                                        delta.currentDecision ? (
                                            <Badge variant="outline" className={`
                                                ${delta.currentDecision === 'prepare_change_order' ? 'border-red-200 bg-red-50 text-red-700' : ''}
                                                ${delta.currentDecision === 'client_handles' ? 'border-orange-200 bg-orange-50 text-orange-700' : ''}
                                                ${delta.currentDecision === 'reduce_other_scope' ? 'border-blue-200 bg-blue-50 text-blue-700' : ''}
                                            `}>
                                                {delta.currentDecision === 'prepare_change_order' && 'Add to CO'}
                                                {delta.currentDecision === 'client_handles' && 'Client Handles'}
                                                {delta.currentDecision === 'reduce_other_scope' && 'Swap Scope'}
                                            </Badge>
                                        ) : (
                                            <Badge variant="secondary" className="opacity-50">Undecided</Badge>
                                        )
                                    )}
                                </td>
                                <td className="p-4 align-top text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        {delta.diff && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => toggleDiff(delta.deltaId)}
                                                title="View Changes"
                                            >
                                                {expandedDiffs.has(delta.deltaId) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                            </Button>
                                        )}
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => onNavigateToConfigItem(delta.configItemId, 'scope-review')}
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </TabsContent>

        <TabsContent value="defects" className="flex-1 overflow-auto bg-white border rounded-md m-0">
             {loading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
            ) : filteredDeltas.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mb-4 opacity-20 text-green-500" />
                    <p>No defects found.</p>
                </div>
            ) : (
                 <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0 z-10">
                        <tr className="border-b">
                            <th className="p-4 text-left font-medium w-[100px]">Severity</th>
                            <th className="p-4 text-left font-medium">Defect Details</th>
                            <th className="p-4 text-left font-medium w-[150px]">Status</th>
                            <th className="p-4 w-[100px]"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {filteredDeltas.map(delta => (
                            <tr key={delta.deltaId}>
                                <td className="p-4 align-top">
                                     <Badge variant={
                                        delta.severity === 'high' ? 'destructive' : 
                                        delta.severity === 'medium' ? 'default' : 'outline'
                                    }>
                                        {delta.severity?.toUpperCase()}
                                    </Badge>
                                </td>
                                <td className="p-4 align-top">
                                    <div className="font-medium text-foreground">{delta.description.replace('Defect: ', '')}</div>
                                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                                        <Badge variant="outline" className="font-normal bg-slate-50">
                                            {delta.configItemName}
                                        </Badge>
                                        {delta.requestedBy && <span>• Reported by {delta.requestedBy}</span>}
                                    </div>
                                    {delta.expectedBehavior && (
                                        <div className="mt-2 grid grid-cols-2 gap-4 text-xs bg-slate-50 p-2 rounded">
                                            <div>
                                                <span className="font-semibold text-green-700">Expected:</span>
                                                <p className="text-slate-600 mt-1">{delta.expectedBehavior}</p>
                                            </div>
                                            <div>
                                                <span className="font-semibold text-red-700">Actual:</span>
                                                <p className="text-slate-600 mt-1">{delta.actualBehavior}</p>
                                            </div>
                                        </div>
                                    )}
                                </td>
                                <td className="p-4 align-top">
                                     <Select 
                                        value={delta.resolutionStatus} 
                                        onValueChange={(val) => handleUpdateDefectStatus(delta.deltaId, val)}
                                     >
                                        <SelectTrigger className={`h-8 w-[140px] ${
                                            delta.resolutionStatus === 'resolved' ? 'text-green-700 bg-green-50 border-green-200' : ''
                                        }`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="open">Open</SelectItem>
                                            <SelectItem value="in_progress">In Progress</SelectItem>
                                            <SelectItem value="resolved">Resolved</SelectItem>
                                            <SelectItem value="rejected">Rejected (Not a Bug)</SelectItem>
                                        </SelectContent>
                                     </Select>
                                </td>
                                <td className="p-4 align-top text-right">
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => onNavigateToConfigItem(delta.configItemId, 'scope-review')}
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                     </tbody>
                 </table>
            )}
        </TabsContent>
      </Tabs>
    </div>
  );
}